import type { ComponentProps } from 'react';
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import type { ResponsiveValue } from 'styled-system';
import { space } from 'styled-system';

import Alert from 'common/components/Alert';
import { Flex } from 'common/components/Flex';
import SellerInformationText from 'common/components/SellerInformationText';
import StickyContainer from 'common/components/StickyContainer';
import { ADD_TO_CART_STICKY_CONTAINER_Z_INDEX } from 'common/constants/z-index';
import { MessageType } from 'common/enums/Alert';
import { Viewport } from 'common/enums/Viewport';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import type { ArticleShop } from 'common/types/Article';
import {
  DrawerInteraction,
  Element as TrackingElement,
  TrackingEventName,
} from 'common/types/tracking';
import { getOptions } from 'common/utils/getStockQuantityOptions';
import { useActivatedCoupon } from '../../article-price/components/hooks';
import FormControls from '../Form/FormControls';
import AddToCartSeparator from './AddToCartSeparator';
import { useSelectedServices } from './hooks/additionalServices';
import { useQuantity } from './hooks/quantity';
import { useSubmitHandler } from './hooks/submit';
import type { AddToCartProps } from './types';
import { getErrorMessage } from './utils';

interface AvailableVariantProps extends Omit<
  AddToCartProps,
  'otherVariantAvailable' | 'onAddToCart' | 'energyClass'
> {
  withSellerKPIs: boolean;
  minWidth?: ResponsiveValue<string | number>;
}

const FORM_CLASS_NAME = 'form-add-to-cart';
const DEFAULT_ADDITIONAL_ELEMENT_OFFSET = 24;

const UnstyledForm = styled(Flex)<any>``;

const Form = (props: ComponentProps<typeof UnstyledForm>) => (
  <UnstyledForm as="form" alignItems="center" flexWrap="wrap" width={1} {...props} />
);

const UnstyledError = styled(Alert)<SpaceProps>`
  ${space};
`;

const Error = (props: ComponentProps<typeof UnstyledError>) => (
  <UnstyledError mb="sp_8" {...props} />
);

const buildFormControlsProps = ({
  stock,
  quantity,
  handleChange,
  isLoading,
  isMounted,
  ...restProps
}) => ({
  isLoading: !isMounted || isLoading,
  isDisabled: !isMounted,
  value: quantity,
  onSelect: handleChange,
  options: getOptions(stock.quantity ?? 0),
  ...restProps,
});

const getTrackingParams = (shop: ArticleShop) => ({
  trackingParams: {
    event: TrackingEventName.AddToCardClick,
    interaction: DrawerInteraction.Open,
    element: TrackingElement.Button,
    button_text: shop?.name,
    ...(shop?.ratings?.average && { rating_average: shop?.ratings?.average.toFixed(1) }),
  },
});

const containerStyles: ComponentProps<typeof Flex> = {
  flexDirection: 'column',
  alignItems: 'flex-end',
  width: '100%',
};

// eslint-disable-next-line max-lines-per-function, complexity
const AvailableVariant = ({
  sellable,
  stock,
  categoryUrl,
  assembly = null,
  cartGrandTotal = 0,
  isMinified,
  selectWidth,
  buttonKind,
  withButtonAnimation = false,
  disableOnSubmit = false,
  alreadyInCart = false,
  noBottomMargin,
  hideQuantity,
  ctaLabel,
  onItemNotSellable,
  bestOffer,
  className,
  withBackground,
  isRounded,
  withIcon,
  materialType,
  disableDrawerOpening,
  minWidth,
  withSellerInfo = false,
  withSellerKPIs = false,
  withStickyAddToCart = false,
  ...restProps
}: AvailableVariantProps) => {
  const { t } = useLocalization();
  const [isMounted, setIsMounted] = useState(false);
  const { value: quantity, handleChange } = useQuantity();
  const selectedServices = useSelectedServices(isMinified);
  const couponCode = useActivatedCoupon();

  const { isLoading, addedToCart, handleSubmit, addToCartErrorMessage } = useSubmitHandler({
    ...restProps,
    bestOffer,
    materialType,
    assembly,
    quantity,
    cartGrandTotal,
    alreadyInCart,
    disableDrawerOpening,
    onItemNotSellable,
    selectedServices,
    couponCode,
  });

  useEffect(() => setIsMounted(true), []);

  const formControlsProps = buildFormControlsProps({
    stock,
    quantity,
    withIcon,
    isMounted,
    isRounded,
    className,
    isLoading,
    buttonKind,
    selectWidth,
    addedToCart,
    hideQuantity,
    ctaLabel,
    materialType,
    disableOnSubmit,
    withButtonAnimation,
    minWidth,
    handleChange,
  });
  const hasSeparator = !isMinified && !withBackground;
  const errorMessage = getErrorMessage(addToCartErrorMessage);
  const isHome24 = bestOffer?.isHome24;

  return (
    <>
      {errorMessage && !isMinified && <Error type={MessageType.Error}>{t(errorMessage)}</Error>}
      <Flex {...containerStyles}>
        <Form
          onSubmit={handleSubmit}
          className={FORM_CLASS_NAME}
          data-testid="add-to-cart"
          {...(noBottomMargin ? { pb: 0 } : {})}
          pb={withBackground ? 'sp_0' : ['sp_0', null, 'sp_8', 'sp_16']}
        >
          {withStickyAddToCart && !isMinified && (
            <StickyContainer
              viewport={Viewport.MobileTablet}
              dependsOn={`.${FORM_CLASS_NAME}`}
              zIndex={[ADD_TO_CART_STICKY_CONTAINER_Z_INDEX, null, 0]}
              additionalElementOffset={24}
              py="sp_16"
            >
              <FormControls {...formControlsProps} isSticky />
            </StickyContainer>
          )}
          <FormControls {...formControlsProps} />
          {withStickyAddToCart && (
            <StickyContainer
              viewport={Viewport.Desktop}
              dependsOn={`.${FORM_CLASS_NAME}`}
              zIndex={[ADD_TO_CART_STICKY_CONTAINER_Z_INDEX, null, 0]}
              additionalElementOffset={DEFAULT_ADDITIONAL_ELEMENT_OFFSET}
              py="sp_20"
              left="50%"
              display="block"
              maxWidth="752px"
              borderRadius="4px 4px 0 0"
              // calc(<sticky container right padding> + <main page right padding>)
              right={[null, null, 'calc(16px + 20px)', 'calc(24px + 24px)']}
            >
              <FormControls {...formControlsProps} isSticky />
            </StickyContainer>
          )}
        </Form>
      </Flex>
      {withSellerInfo && (
        <SellerInformationText
          textLabel={t('h24_sold_by')}
          isHome24={isHome24}
          withSellerKPIs={withSellerKPIs}
          mt="sp_16"
          mb="sp_0"
          {...bestOffer?.shop}
          {...getTrackingParams(bestOffer?.shop)}
        />
      )}
      {hasSeparator && <AddToCartSeparator />}
    </>
  );
};

export default AvailableVariant;
